import type { CampaignResponse, EmailQuotaStatus, TestResult } from "@voice2mail/shared/v2m_api";
import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "../api/client";
import { QuotaStatus } from "./QuotaStatus";

type WizardStep = "create" | "review" | "confirm";

interface CampaignWizardProps {
  /** Called when dispatch is complete and the caller should show the progress view. */
  onDispatched: (campaignId: string) => void;
  onCancel: () => void;
}

const PLAN_TIERS = ["free", "basic", "pro", "enterprise"];
const COOLDOWN_SECONDS = 30;

// --- List manipulation helpers (pure, no component state) ---

function addItem(list: string[], setList: (val: string[]) => void) {
  setList([...list, ""]);
}

function removeItem(index: number, list: string[], setList: (val: string[]) => void) {
  setList(list.filter((_, i) => i !== index));
}

function updateItem(
  index: number,
  value: string,
  list: string[],
  setList: (val: string[]) => void
) {
  const updated = [...list];
  updated[index] = value;
  setList(updated);
}

interface ChangeListProps {
  label: string;
  items: string[];
  setItems: (val: string[]) => void;
}

function ChangeList({ label, items, setItems }: ChangeListProps) {
  return (
    <div className="mb-5">
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      {items.map((item, idx) => (
        // eslint-disable-next-line react-doctor/no-array-index-as-key -- user-typed dynamic string list with no stable id; the value itself changes while typing, so index is the only stable key
        <div key={idx} className="flex gap-2 mb-2">
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={item}
            aria-label={`${label} item ${idx + 1}`}
            onChange={(e) => updateItem(idx, e.target.value, items, setItems)}
            placeholder={`Enter ${label.toLowerCase().replace(" changes", "")} change...`}
          />
          {items.length > 1 && (
            <button
              type="button"
              className="text-sm text-red-600 hover:text-red-800 px-2 transition-colors"
              onClick={() => removeItem(idx, items, setItems)}
              aria-label={`Remove ${label} item ${idx + 1}`}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
        onClick={() => addItem(items, setItems)}
      >
        + Add change
      </button>
    </div>
  );
}

export function CampaignWizard({ onDispatched, onCancel }: CampaignWizardProps) {
  const [step, setStep] = useState<WizardStep>("create");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [template, setTemplate] = useState("legal_change");
  const [documentVersion, setDocumentVersion] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [ppChanges, setPpChanges] = useState<string[]>([""]);
  const [tosChanges, setTosChanges] = useState<string[]>([""]);
  const [audienceAll, setAudienceAll] = useState(true);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [quota, setQuota] = useState<EmailQuotaStatus | null>(null);

  // Step 2 state
  const [campaign, setCampaign] = useState<CampaignResponse | null>(null);

  // Step 3 state
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [countdown, setCountdown] = useState(COOLDOWN_SECONDS);
  const [dispatching, setDispatching] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchQuota = useCallback(async () => {
    try {
      const data = await api.getEmailQuota();
      setQuota(data);
    } catch {
      // Quota fetch failure is non-critical; user can still proceed.
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  // Cleanup countdown timer
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleTierToggle = (tier: string) => {
    setSelectedTiers((prev) =>
      prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier]
    );
  };

  // --- Step 1: Create Campaign ---

  const handleCreateCampaign = async () => {
    setError(null);

    if (!documentVersion.trim()) {
      setError("Document version (id@revision) is required.");
      return;
    }

    if (!effectiveDate.trim()) {
      setError("Effective date is required.");
      return;
    }

    const filteredPp = ppChanges.filter((c) => c.trim());
    const filteredTos = tosChanges.filter((c) => c.trim());

    if (filteredPp.length === 0 && filteredTos.length === 0) {
      setError("Add at least one Privacy Policy or ToS change.");
      return;
    }

    setLoading(true);
    try {
      const result = await api.createCampaign({
        template,
        document_version: documentVersion.trim(),
        params: {
          effective_date: effectiveDate,
          pp_changes: filteredPp,
          tos_changes: filteredTos,
        },
        audience_filter:
          audienceAll || selectedTiers.length === 0 ? null : { plan_tier: selectedTiers },
      });
      setCampaign(result);
      setStep("review");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: Send Test ---

  const handleSendTest = async () => {
    if (!campaign) return;

    setLoading(true);
    setError(null);
    try {
      const results = await api.sendTestEmail(campaign.id);
      setTestResults(results);
      setStep("confirm");
      startCountdown();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send test email");
    } finally {
      setLoading(false);
    }
  };

  // --- Step 3: Countdown + Dispatch ---

  const startCountdown = () => {
    setCountdown(COOLDOWN_SECONDS);
    if (countdownRef.current) clearInterval(countdownRef.current);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleDispatch = async () => {
    if (!campaign) return;

    setDispatching(true);
    setError(null);
    try {
      await api.dispatchCampaign(campaign.id);
      onDispatched(campaign.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to dispatch campaign");
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Step indicator */}
      <div className="flex gap-2 mb-6">
        {(["create", "review", "confirm"] as const).map((s) => (
          <div
            key={s}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === s ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
            }`}
          >
            {s === "create" ? 1 : s === "review" ? 2 : 3}
          </div>
        ))}
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* STEP 1: Create */}
      {step === "create" && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Create Campaign</h2>

          <div className="mb-5">
            <label
              className="block text-sm font-medium text-slate-700 mb-2"
              htmlFor="campaign-template"
            >
              Template
            </label>
            <select
              id="campaign-template"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
            >
              <option value="legal_change">Legal Change Notification</option>
            </select>
          </div>

          <div className="mb-5">
            <label
              className="block text-sm font-medium text-slate-700 mb-2"
              htmlFor="campaign-document-version"
            >
              Document Version
            </label>
            <input
              id="campaign-document-version"
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={documentVersion}
              onChange={(e) => setDocumentVersion(e.target.value)}
              placeholder="e.g., tos@2026-08-09"
            />
            <p className="text-xs text-slate-500 mt-1">
              Idempotency key (id@revision). Calling create again with the SAME value reuses this
              campaign and only queues newly-eligible recipients instead of duplicating it.
            </p>
          </div>

          <div className="mb-5">
            <label
              className="block text-sm font-medium text-slate-700 mb-2"
              htmlFor="campaign-effective-date"
            >
              Effective Date
            </label>
            <input
              id="campaign-effective-date"
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              placeholder="e.g., March 15, 2026"
            />
          </div>

          <ChangeList label="Privacy Policy Changes" items={ppChanges} setItems={setPpChanges} />
          <ChangeList
            label="Terms of Service Changes"
            items={tosChanges}
            setItems={setTosChanges}
          />

          <div className="mb-5">
            <span className="block text-sm font-medium text-slate-700 mb-2">Audience</span>
            <div className="mb-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={audienceAll}
                  onChange={(e) => setAudienceAll(e.target.checked)}
                />
                All Users
              </label>
            </div>
            {!audienceAll && (
              <div className="flex flex-wrap gap-4 pl-6">
                {PLAN_TIERS.map((tier) => (
                  <label key={tier} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={selectedTiers.includes(tier)}
                      onChange={() => handleTierToggle(tier)}
                    />
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="mb-6">
            <QuotaStatus quota={quota} />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
              onClick={handleCreateCampaign}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Review & Test */}
      {step === "review" && campaign && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Review & Test</h2>

          <div className="flex gap-6 mb-6 text-sm">
            <span>
              Recipients: <strong>{campaign.totalRecipients}</strong>
            </span>
            <span>
              Template: <strong>{campaign.template}</strong>
            </span>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              onClick={() => setStep("create")}
            >
              Back
            </button>
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
              onClick={handleSendTest}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Test Email"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Confirm */}
      {step === "confirm" && campaign && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Confirm Dispatch</h2>

          <div className="mb-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Test Results</h3>
            {testResults.map((r) => (
              <div key={r.email} className="flex justify-between text-sm py-1">
                <span>{r.email}</span>
                <span className={r.success ? "text-green-600" : "text-red-600"}>
                  {r.success ? "Sent" : "Failed"}
                </span>
              </div>
            ))}
          </div>

          <p className="text-sm text-slate-600 mb-6">
            Check your inbox to verify the email looks correct before dispatching to all{" "}
            {campaign.totalRecipients} recipients.
          </p>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              onClick={() => setStep("review")}
            >
              Back
            </button>
            <button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
              onClick={handleDispatch}
              disabled={countdown > 0 || dispatching}
            >
              {dispatching
                ? "Dispatching..."
                : countdown > 0
                  ? `Send to All ${campaign.totalRecipients} Users (${countdown}s)`
                  : `Send to All ${campaign.totalRecipients} Users`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
